import type { EngineState, ButtonId } from './types';
import { PI_2, SF2_URL, clamp } from './constants';
import { loadAllData } from './data/loader';
import { Store } from './state/store';
import { loadSettings, saveSettings } from './state/settings';
import { Engine } from './audio/engine';
import { Scheduler } from './audio/scheduler';
import { setupCanvas } from './render/canvas';
import { loadButtonImages } from './render/buttons';
import { startRenderLoop } from './render/loop';
import { installPointer } from './input/pointer';
import { installKeyboard } from './input/keyboard';
import { MotionSource } from './input/motion';
import { installAbout } from './ui/about';
import { tryRequestFullscreen, tryLockLandscape } from './ui/fullscreen';
import { pickSynth, preferenceFromUrl, withTimeout } from './audio/createSynth';
import type { SynthStep } from './audio/synth';

const STEP_TIMEOUT_MS = 30_000;

// Best-effort: enter fullscreen, THEN lock to landscape. The lock MUST run after
// fullscreen resolves — Chrome/Android reject screen.orientation.lock() unless the
// document is already fullscreen, so firing both as independent un-awaited promises
// (as the call site used to) leaves the lock a silent no-op on Android. Chaining
// them here fixes that. iOS Safari supports neither the lock nor (older) fullscreen,
// so this degrades to a harmless no-op there. Call WITHOUT awaiting at the start-button
// gesture so it never blocks motion.start()'s synchronous permission dispatch.
async function lockLandscape(): Promise<void> {
  await tryRequestFullscreen(document.documentElement);
  await tryLockLandscape();
}

const STEP_LABEL: Record<SynthStep, string> = {
  'load-fluidsynth': 'Loading FluidSynth WASM…',
  'load-worklet': 'Loading audio worklet…',
  'create-node': 'Creating synthesizer node…',
  'create-sequencer': 'Creating MIDI sequencer…',
  'load-soundfont': 'Loading SoundFont into synthesizer…',
  ready: 'Ready.',
};

async function fetchSoundFontWithProgress(
  url: string,
  onProgress?: (pct: number) => void,
): Promise<ArrayBuffer> {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Failed to fetch ${url}: ${resp.status}`);
  const total = Number(resp.headers.get('content-length')) || 0;
  if (!resp.body || total === 0) return resp.arrayBuffer();
  const reader = resp.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      received += value.byteLength;
      onProgress?.(total > 0 ? received / total : 0);
    }
  }
  const buf = new Uint8Array(received);
  let offset = 0;
  for (const c of chunks) {
    buf.set(c, offset);
    offset += c.byteLength;
  }
  return buf.buffer;
}

function showMotionRetry(motion: MotionSource): void {
  // Surface a visible banner + retry button when motion permission was denied
  // or wasn't available. Tapping retry creates a fresh user-gesture context,
  // which is what iOS Safari requires to re-prompt for "Motion & Orientation
  // Access."
  if (document.getElementById('motion-retry')) return;
  const banner = document.createElement('div');
  banner.id = 'motion-retry';
  banner.style.cssText =
    'position:fixed;top:8px;right:8px;z-index:20;background:rgba(120,30,30,0.92);' +
    'color:#fff;font:13px/1.4 -apple-system,BlinkMacSystemFont,sans-serif;' +
    'padding:10px 12px;border-radius:6px;max-width:280px;border:1px solid #f88;';
  banner.innerHTML =
    '<div style="font-weight:bold;margin-bottom:4px;">Motion sensor inactive</div>' +
    '<div style="opacity:0.85;font-size:11px;margin-bottom:8px;">' +
    'iPhone/iPad: open Settings → Safari → enable "Motion & Orientation Access," ' +
    'then return here and tap Retry.</div>' +
    '<button id="motion-retry-btn" style="background:#fff;color:#000;border:0;' +
    'padding:6px 12px;border-radius:4px;font-weight:bold;cursor:pointer;">Retry</button>' +
    '<button id="motion-retry-dismiss" style="background:transparent;color:#fff;' +
    'border:1px solid rgba(255,255,255,0.4);padding:6px 10px;margin-left:6px;' +
    'border-radius:4px;cursor:pointer;">Dismiss</button>';
  document.body.appendChild(banner);
  const btn = banner.querySelector('#motion-retry-btn') as HTMLButtonElement | null;
  const dismiss = banner.querySelector('#motion-retry-dismiss') as HTMLButtonElement | null;
  btn?.addEventListener('click', async () => {
    btn.disabled = true;
    btn.textContent = 'Requesting…';
    const mode = await motion.retry().catch(() => 'idle' as const);
    if (mode === 'device') {
      banner.remove();
    } else {
      btn.disabled = false;
      btn.textContent = 'Retry';
      const note = document.createElement('div');
      note.style.cssText = 'font-size:11px;opacity:0.8;margin-top:6px;';
      note.textContent = `Still ${mode}. Check iOS Settings → Safari.`;
      banner.appendChild(note);
    }
  });
  dismiss?.addEventListener('click', () => banner.remove());
}

function showFatal(progress: HTMLElement, msg: string, detail?: unknown): void {
  console.error('Fatal:', msg, detail);
  progress.hidden = false;
  const detailStr = detail instanceof Error ? `${detail.message}` : detail ? String(detail) : '';
  progress.innerHTML = `<div style="color:#f88;font-weight:bold;">${msg}</div>` +
    (detailStr ? `<div style="opacity:0.8;font-size:11px;margin-top:6px;">${detailStr}</div>` : '') +
    `<div style="opacity:0.6;font-size:11px;margin-top:6px;">Try <a style="color:#9cf;" href="?synth=main">?synth=main</a> for the main-thread fallback.</div>`;
}

async function boot(): Promise<void> {
  const canvas = document.getElementById('canvas') as HTMLCanvasElement;
  const overlay = document.getElementById('overlay') as HTMLDivElement;
  const startBtn = document.getElementById('start-button') as HTMLButtonElement;
  const progress = document.getElementById('progress') as HTMLDivElement;

  const settings = loadSettings();
  const initial: EngineState = {
    playVar: 0,
    playBar: 0,
    playEvent: 0,
    jumpVar: 0,
    jumpBar: 0,
    isPlaying: false,
    pitch: 0,
    roll: 0,
    ...settings,
  };
  const store = new Store<EngineState>(initial);

  const cc = setupCanvas(canvas);
  let hoverId: ButtonId | null = null;
  let imagesReady = false;
  let images = null as Awaited<ReturnType<typeof loadButtonImages>> | null;
  loadButtonImages()
    .then((imgs) => {
      images = imgs;
      imagesReady = true;
    })
    .catch((err) => console.warn('Button images failed to load:', err));

  const stub = new Image();
  const stubImages = {
    about: stub, linear: stub, random: stub, loop: stub,
    noLoop: stub, tempoLo: stub, tempoHi: stub,
  };
  startRenderLoop(cc, {
    getState: () => store.get(),
    getHover: () => hoverId,
    get images() {
      return imagesReady && images ? images : stubImages;
    },
  } as Parameters<typeof startRenderLoop>[1]);

  // Surface unhandled errors so they don't disappear into the void on mobile.
  window.addEventListener('error', (e) => showFatal(progress, 'Unhandled error', e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) =>
    showFatal(progress, 'Unhandled promise rejection', e.reason),
  );

  startBtn.addEventListener(
    'click',
    async () => {
      startBtn.disabled = true;
      startBtn.textContent = 'Loading…';

      try {
        // CRITICAL ORDER: motion.start() must be invoked synchronously, BEFORE
        // any `await`, so that DeviceOrientationEvent.requestPermission() lands
        // inside the user-activation window. Any prior await consumes the
        // gesture and iOS Safari silently rejects the permission prompt.
        const motion = new MotionSource((roll, pitch) => store.set({ roll, pitch }));
        const motionStartPromise = motion.start().catch((err) => {
          console.warn('Motion start failed:', err);
          return 'idle' as const;
        });
        // Fullscreen + landscape lock also want the gesture; dispatched here without
        // await so they run alongside the motion permission rather than blocking it.
        // lockLandscape() internally chains fullscreen → lock so the lock isn't fired
        // before the document is fullscreen (Android rejects it otherwise).
        void lockLandscape();

        const ctx = new AudioContext();
        try {
          await ctx.resume();
        } catch (err) {
          console.warn('AudioContext.resume failed:', err);
        }

        const motionMode = await motionStartPromise;
        console.log('[motion] mode:', motionMode);

        progress.hidden = false;
        progress.textContent = 'Loading data…';

        const [data, sf2] = await Promise.all([
          loadAllData(),
          fetchSoundFontWithProgress(SF2_URL, (pct) => {
            progress.textContent = `Loading SoundFont… ${Math.round(pct * 100)}%`;
          }),
        ]);

        const synth = pickSynth(preferenceFromUrl(), ctx);
        progress.textContent = `Initializing synthesizer (${synth.backend})…`;

        const onStep = (step: SynthStep): void => {
          progress.textContent = `[${synth.backend}] ${STEP_LABEL[step]}`;
        };

        await withTimeout(synth.init(ctx, onStep), STEP_TIMEOUT_MS, 'synth.init');
        await withTimeout(
          synth.loadSoundFont(sf2, onStep),
          STEP_TIMEOUT_MS,
          'synth.loadSoundFont',
        );

        console.log('[synth] backend:', synth.backend, 'ctx.state:', ctx.state, 'sampleRate:', ctx.sampleRate);

        const engine = new Engine(data.variations, data.offsets, store);
        engine.reset();
        const scheduler = new Scheduler(ctx, synth, engine);

        // Pitch → master volume. Tilting forward (top of phone away) → loud.
        // pitch ∈ [-π/2, π/2] linearly maps to gain ∈ [0, 1].
        const pitchToGain = (pitch: number): number => clamp((pitch + PI_2) / Math.PI, 0, 1);
        synth.setMasterGain(pitchToGain(store.get().pitch));
        store.subscribe((s) => synth.setMasterGain(pitchToGain(s.pitch)));

        const motionDebug = document.getElementById('motion-debug');
        if (motionDebug) {
          const tick = (): void => {
            const s = store.get();
            const d = motion.getDiagnostics();
            const now = performance.now();
            const orAge = d.orientationLastMs > 0 ? Math.round(now - d.orientationLastMs) : -1;
            const moAge = d.motionLastMs > 0 ? Math.round(now - d.motionLastMs) : -1;
            const vv = window.visualViewport;
            motionDebug.textContent =
              `mode: ${d.mode}  api: o=${d.hasOrientationApi ? 'y' : 'n'} m=${d.hasMotionApi ? 'y' : 'n'}  grant: o=${d.orientationGranted ? 'y' : 'n'} m=${d.motionGranted ? 'y' : 'n'}\n` +
              `orient evt=${d.orientationCount} age=${orAge}ms beta=${d.rawBeta.toFixed(1)} gamma=${d.rawGamma.toFixed(1)}\n` +
              `motion evt=${d.motionCount} age=${moAge}ms ax=${d.rawAccelX.toFixed(2)} ay=${d.rawAccelY.toFixed(2)} az=${d.rawAccelZ.toFixed(2)}\n` +
              `screen=${d.screenAngle}deg vv=${vv ? `${Math.round(vv.width)}x${Math.round(vv.height)}` : 'n/a'} win=${window.innerWidth}x${window.innerHeight}\n` +
              `pitch=${s.pitch.toFixed(2)} roll=${s.roll.toFixed(2)} gain=${pitchToGain(s.pitch).toFixed(2)} speed=${engine.speedFromState().toFixed(2)} invert=${s.invertPitch ? 'on' : 'off'}`;
            requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }

        // iOS-specific recovery affordances when sensor permission was denied
        // or unavailable. Tapping the retry button creates a fresh user-gesture
        // window and re-runs motion.start().
        const isTouchDevice = 'ontouchstart' in window;
        if (isTouchDevice && (motionMode === 'denied' || motionMode === 'mouse' || motionMode === 'idle')) {
          showMotionRetry(motion);
        }

        // Re-arm sensor delivery whenever the about dialog closes — iOS WebKit
        // sometimes stalls events while a modal is open or after dismissal.
        const aboutDialog = document.getElementById('about') as HTMLDialogElement | null;
        aboutDialog?.addEventListener('close', () => motion.refresh());
        aboutDialog?.addEventListener('cancel', () => motion.refresh());

        installPointer(canvas, {
          getLayout: () => cc.getLayout(),
          onButton: (id) => {
            const s = store.get();
            switch (id) {
              case 'loop': {
                const v = !s.loop;
                engine.setLoop(v);
                saveSettings({ loop: v });
                break;
              }
              case 'linear': {
                const v = !s.linear;
                engine.setLinear(v);
                saveSettings({ linear: v });
                break;
              }
              case 'invert': {
                const v = !s.invertPitch;
                engine.setInvertPitch(v);
                saveSettings({ invertPitch: v });
                break;
              }
              case 'about':
                about.open();
                break;
            }
          },
          onGridTap: (bar, varIdx) => engine.requestJump(bar, varIdx),
          onHoverChange: (id) => {
            hoverId = id;
          },
        });

        const DEFAULT_FONT_LABEL = 'Roland Nice Piano (default)';
        const about = installAbout({
          // Swap in a user-supplied .sf2 live; the synth keeps the current font
          // if the new one fails to load, and the scheduler keeps playing.
          onLoadFont: async (file) => {
            const buf = await file.arrayBuffer();
            await synth.loadSoundFont(buf);
            about.setFontName(file.name);
          },
          onResetFont: async () => {
            const buf = await fetchSoundFontWithProgress(SF2_URL);
            await synth.loadSoundFont(buf);
            about.setFontName(DEFAULT_FONT_LABEL);
          },
        });
        about.setFontName(DEFAULT_FONT_LABEL);
        installKeyboard(engine, scheduler, store, {
          openAbout: about.open,
          closeAbout: about.close,
        });

        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            ctx.resume().catch(() => {});
          }
        });

        progress.hidden = true;
        overlay.style.display = 'none';
        scheduler.start();
      } catch (err) {
        showFatal(progress, 'Failed to start audio', err);
        startBtn.disabled = false;
        startBtn.textContent = 'Tap to start';
      }
    },
    { once: false },
  );
}

boot().catch((err) => {
  console.error('boot failed:', err);
  const progress = document.getElementById('progress');
  if (progress) showFatal(progress, 'Boot failed', err);
});
